"use client";

// ============================================================
// ANSICHTSSACHE-AGENT – Der Ticket-Reporter liegt über allem
// ============================================================
// Gemeldet wird oft genau dann, wenn ein Dialog offen ist: der
// Fehler steckt ja meist darin. Bis zum 23.09.2026 war der Ticket-Reporter
// dann nicht zu bedienen — die Leiste ist `sticky z-20` und
// bildet einen eigenen Stapel, jeder Dialog (`z-50`) und jedes
// Seitenblatt lag mit seiner Abdunklung darüber, Knopf und
// Fenster samt `z-[100]` darunter.
//
// Was hier drinsteht, wird deshalb an den Rumpf gehängt und
// bekommt, was ein Dialog und ein Seitenblatt einem Fenster
// darüber sonst verweigern:
//
//   ZEIGER. Das Seitenblatt (Radix) setzt `pointer-events: none`
//   auf den Rumpf; ohne `pointer-events-auto` ginge jeder Klick
//   durch den Ticket-Reporter hindurch.
//
//   KEIN „DANEBEN". `Branch` meldet den Bereich bei Radix als
//   zugehörig an — ein Klick oder Fokus hier schließt das
//   Seitenblatt darunter nicht. So macht es Radix selbst mit den
//   Toasts.
//
//   FOKUS (nur mit `fokus`). Das Seitenblatt fängt den Fokus ein
//   und holt ihn bei jedem Wechsel nach draußen zurück; ins Feld
//   „Titel" käme man nie. Ein eigener `FocusScope` stellt die
//   Falle darunter ruhig, solange das Fenster offen ist.
//
//   ESCAPE. Dialog und Seitenblatt hören auf dem Dokument, und
//   ohne Weiteres schlösse Escape im Ticket-Reporter den Dialog darunter.
//   Ein Escape von hier wird deshalb vorab als genommen markiert
//   (`preventDefault`); beide lassen es dann liegen.
//
//   RAD. Die Scrollsperre des Seitenblatts schluckt jedes Rad
//   außerhalb des Blatts — die Ticketliste ließe sich nicht mehr
//   rollen. Das Ereignis endet deshalb hier.
// ============================================================

import { useEffect, useRef, useSyncExternalStore } from "react";
import { createPortal } from "react-dom";
import { Branch } from "@radix-ui/react-dismissable-layer";
import { FocusScope } from "@radix-ui/react-focus-scope";
import { cn } from "@/lib/utils";

/** Kennzeichen aller Flächen des Ticket-Reporters, die am Rumpf hängen. */
export const TICKET_REPORTER = "data-ticket-reporter";

/** Liegt das Ziel in einer Fläche des Ticket-Reporters? */
export function inTicketReporter(ziel: EventTarget | null): boolean {
  return ziel instanceof Element && ziel.closest(`[${TICKET_REPORTER}]`) !== null;
}

const nieAendern = () => () => {};

export function UeberAllem({
  fokus = false,
  onEscape,
  className,
  children,
  ...rest
}: React.HTMLAttributes<HTMLDivElement> & {
  /** Fenster und Markierung: Fokus halten, Escape nehmen. */
  fokus?: boolean;
  onEscape?: () => void;
}) {
  const flaeche = useRef<HTMLDivElement | null>(null);
  const vorher = useRef<HTMLElement | null>(null);

  // Am Server gibt es keinen Rumpf. Beim Hydrieren muss der
  // Browser dasselbe liefern wie der Server — nichts —, sonst
  // sucht React das Portal an der Stelle, an der am Server schon
  // das nächste Element steht. Erst danach hängt es sich an.
  const hydriert = useSyncExternalStore(
    nieAendern,
    () => true,
    () => false,
  );

  useEffect(() => {
    const knoten = flaeche.current;
    if (!knoten) return undefined;

    // Vor allen anderen: im Fangschritt am Fenster, noch bevor
    // Dialog und Radix auf dem Dokument davon hören.
    function taste(e: KeyboardEvent) {
      if (e.key === "Escape" && onEscape && knoten?.contains(e.target as Node)) e.preventDefault();
    }
    function rad(e: Event) {
      e.stopPropagation();
    }

    window.addEventListener("keydown", taste, true);
    knoten.addEventListener("wheel", rad);
    knoten.addEventListener("touchmove", rad);
    return () => {
      window.removeEventListener("keydown", taste, true);
      knoten.removeEventListener("wheel", rad);
      knoten.removeEventListener("touchmove", rad);
    };
  }, [onEscape, hydriert]);

  if (!hydriert) return null;

  const inhalt = (
    <Branch asChild>
      <div
        {...rest}
        ref={flaeche}
        {...{ [TICKET_REPORTER]: "" }}
        className={cn("pointer-events-auto outline-none", className)}
        onKeyDown={(e) => {
          rest.onKeyDown?.(e);
          // Was ein Feld darin selbst mit Escape erledigt (den
          // Verweis im Editor verwerfen), hält das Ereignis an und
          // kommt hier nicht an. Erst das nächste schließt.
          if (e.key === "Escape" && onEscape) onEscape();
        }}
      >
        {children}
      </div>
    </Branch>
  );

  return createPortal(
    fokus ? (
      <FocusScope
        asChild
        // Der Fokus rückt auf die Fläche selbst, nicht auf den
        // ersten Knopf darin: Escape wirkt dann sofort, und kein
        // Knopf trägt einen Ring, den niemand angesteuert hat.
        onMountAutoFocus={(e) => {
          e.preventDefault();
          vorher.current = document.activeElement as HTMLElement | null;
          flaeche.current?.focus({ preventScroll: true });
        }}
        // Zurück an den Ausgangspunkt nur, wenn der Fokus sonst
        // verloren wäre. Wer das Fenster mit einem Klick in ein
        // Feld des Dialogs schließt, will DORT schreiben.
        //
        // Und von Hand statt durch Radix: Radix markiert dabei den
        // ganzen Text im Feld. Nach der Markierung stand so der Titel
        // ausgewählt da, und der nächste Buchstabe hätte ihn gelöscht.
        onUnmountAutoFocus={(e) => {
          e.preventDefault();
          const aktiv = document.activeElement;
          if (aktiv && aktiv !== document.body) return;
          if (vorher.current?.isConnected) vorher.current.focus({ preventScroll: true });
        }}
      >
        {inhalt}
      </FocusScope>
    ) : (
      inhalt
    ),
    document.body,
  );
}
